import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import authApiService from "../../services/authServices";
import { ThreeDots } from "react-loader-spinner";
import { jwtDecode } from "jwt-decode";
import {
  didWinSet,
  resolveTiebreakSuperscript,
} from "../../utils/scorecardDisplay";
import { formatSeasonDisplayName } from "../../utils/seasonName";


const Records = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [recordError, setRecordError] = useState("");
  const [loading, setIsLoading] = useState(true);

  const location = useLocation();
  const { memberId, recordType: recordTypeFromState } = location.state || {};
  // overall = career record (persists after season clear); tournament = current season only
  const recordType =
    recordTypeFromState === "tournament" ? "tournament" : "overall";
  const isTournamentRecord = recordType === "tournament";
  const recordLabel = isTournamentRecord
    ? "Tournament Record"
    : "Overall Record";
  
  // Admin role check ke liye
  const userRole = useMemo(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken?.role || null;
    } catch (error) {
      console.error('Error decoding token for role:', error);
      return null;
    }
  }, []);
  
  const isAdmin = userRole === 1;

  const getRecord = async () => {
    if (!memberId) {
      setRecords([]);
      setSummary(null);
      setRecordError("");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setRecordError("");
      // Tournament = active season matches; Overall = career totals (persist after clear season)
      const recordData = isTournamentRecord
        ? await authApiService.get(`member-season-records/${memberId}`)
        : await authApiService.get(`member-overall-records/${memberId}`);
      console.log("=== RECORDS DEBUG ===");
      console.log("recordType:", recordType);
      console.log("Full API response:", recordData);
      console.log("isAdmin:", isAdmin);
      console.log("userRole:", userRole);
      console.log("records array:", recordData?.data?.records);

      const rawRecords = recordData?.data?.records || [];
      const sorted = [...rawRecords].sort((a, b) => (b.playedAt || 0) - (a.playedAt || 0));
      setRecords(sorted);
      setSummary(recordData?.data?.summary || null);
      
      // Debug: Check if matchId exists in records
      if (recordData?.data?.records && Array.isArray(recordData.data.records)) {
        recordData.data.records.forEach((record, index) => {
          console.log(`Record ${index}:`, {
            id: record.id,
            matchId: record.matchId,
            match_id: record.match_id,
            fullRecord: record
          });
        });
      }
    } catch (error) {
      console.error("Error fetching member records:", error);
      setRecords([]);
      setSummary(null);
      setRecordError(
        isTournamentRecord
          ? "Unable to load this tournament's scorecards."
          : "Unable to load all-time scorecards. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecord();
  }, [memberId, recordType]);

  if (loading) {
    return (
      <div className="page-container col-sm-9">
        <div
          className="loader-container"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#4fa94d"
            ariaLabel="three-dots-loading"
            visible={true}
          />
        </div>
      </div>
    );
  }

  // Backend me playoff matches identify karne ka robust logic.
  // 1) `bracketEntry` (most reliable) - agar present ho
  // 2) `round` >= 5 (fallback)
  // 3) `course` me "playoff" word (fallback)
  const isPlayoffMatch = (match) => {
    const bracketEntryValue = match?.bracketEntry ?? match?.bracket_entry;
    const bracketFlag =
      bracketEntryValue != null && Number.isFinite(Number(bracketEntryValue)) && Number(bracketEntryValue) > 0;

    const roundValue = Number(match?.round);
    const roundFlag = Number.isFinite(roundValue) && roundValue >= 5;

    const courseFlag = /playoff/i.test(String(match?.course || ""));
    return bracketFlag || roundFlag || courseFlag;
  };

  const sortByPlayedAtDesc = (a, b) => {
    const aTime = new Date(a?.playedAt || 0).getTime();
    const bTime = new Date(b?.playedAt || 0).getTime();
    return bTime - aTime;
  };

  const playoffRecords = records
    .filter((r) => isPlayoffMatch(r))
    .sort(sortByPlayedAtDesc);
  const regularRecords = records
    .filter((r) => !isPlayoffMatch(r))
    .sort(sortByPlayedAtDesc);
  const displayRecords = [...playoffRecords, ...regularRecords];
  const regularCount = regularRecords.length;
  const playoffCount = playoffRecords.length;

  const formatSectionDate = (playedAt) => {
    if (!playedAt) return "";
    const d = new Date(playedAt);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  };

  const regularSectionDate = formatSectionDate(regularRecords[0]?.playedAt);
  const playoffSectionDate = formatSectionDate(playoffRecords[0]?.playedAt);
  const regularSectionTitle = regularSectionDate
    ? `Regular Season ${regularSectionDate}`
    : "Regular Season";
  const playoffSectionTitle = playoffSectionDate
    ? `Playoff Season ${playoffSectionDate}`
    : "Playoff Season";

  const playerDisplayName =
    summary?.player?.name?.trim() ||
    records[0]?.player?.name?.trim() ||
    "Player";
  const summaryWins = summary?.wins ?? 0;
  const summaryLosses = summary?.losses ?? 0;

  return (
    <div className="page-container record-view-page col-sm-9" style={{ overflowX: 'hidden' }}>
      <div className="record-summary-bar" role="region" aria-label={`${recordLabel} summary`}>
        <span className="record-summary-name">{playerDisplayName}</span>&nbsp;&nbsp;
        <span className="record-summary-label">{recordLabel}</span>&nbsp;&nbsp;
        <span className="record-summary-scorebox" aria-label={`Record ${summaryWins} wins ${summaryLosses} losses`}>
          {summaryWins}-{summaryLosses}
        </span>
      </div>
      {recordError && (
        <div className="alert alert-danger" role="alert">
          {recordError}
        </div>
      )}

      <div className="clear" />

      <div className="record-section pb-5">
        <div className="record-section-container row">
          {displayRecords.map((match, index) => {
            // matchId can be match.matchId, match.id, or match.match_id - check all
            const actualMatchId = match.matchId || match.match_id || match.id;
            const opponentId = match.opponent?.id || match.opponentId || match.opponent_id;
            const opponentName = match.opponent?.name || match.opponentName || match.opponent_name || 'Player';

            const splitSeasonName = (fullName) => {
              if (!fullName) return { top: '', side: '' };
              const keywords = ['Singles', 'Doubles', 'Mixed'];
              let idx = -1;
              for (const key of keywords) {
                const i = fullName.indexOf(key);
                if (i !== -1) {
                  idx = i;
                  break;
                }
              }
              if (idx === -1) {
                return { top: fullName, side: '' };
              }
              const st1 = fullName.slice(0, idx).trim();
              const st2 = fullName.slice(idx).trim();
              return { top: st1, side: st2 };
            };

            const { top: seasonTop, side: seasonSide } = splitSeasonName(
              formatSeasonDisplayName(match.course)
            );
            const seasonFullName = [seasonTop, seasonSide].filter(Boolean).join(" ");
            // Card label should remain season/division based; playoff grouping is handled by headers.
            
            // console.log is intentionally removed for cleaner UI performance.
            
            return (
            <React.Fragment key={match.id || index}>
              {index === 0 && playoffCount > 0 && (
                <div
                  className="col-12 seasonDate"
                  style={{
                   
                    color: "#333",
                    padding: "10px 14px",
                    fontWeight: 700,
                    borderRadius: 4,
                    marginBottom: 16,
                  }}
                >
                  <span>{playoffSectionTitle}</span>
                </div>
              )}

              {index === playoffCount && regularCount > 0 && (
                <div
                  className="col-12 seasonDate"
                  style={{
                  
                    color: "#333",
                    padding: "10px 14px",
                    fontWeight: 700,
                    borderRadius: 4,
                    marginBottom: 16,
                  }}
                >
                  <span>{regularSectionTitle}</span>
                </div>
              )}

              <div className="match col-lg-6 col-md-6 col-xxl-4">
              {/* Admin Edit Button - Top position - ALWAYS SHOW FOR DEBUG */}
              {isAdmin && (
                <div style={{ 
                  textAlign: 'right', 
                  marginBottom: '5px',
                  marginRight: '10px'
                }}>
                  {actualMatchId ? (
                    <Link
                      to={`/carter/schedule/postresult?matchId=${actualMatchId}&userId=${opponentId}&editMode=true&full_name=${encodeURIComponent(opponentName)}&returnPage=members-record&returnMemberId=${memberId || ''}&recordType=${recordType}`}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#666',
                        padding: '3px 10px',
                        borderRadius: '3px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        display: 'inline-block',
                        fontWeight: '500'
                      }}
                    >
                      Edit
                    </Link>
                  ) : (
                    <span style={{ color: 'red', fontSize: '11px' }}>No Match ID</span>
                  )}
                </div>
              )}
              <div className="component-page">
                <div className="mt-5">
                  <div className="hd-title">
                    <div className="position-relative mt-40">
                      {/* Left vertical label - division (st2) */}
                      <div className="selected-country">
                        <p
                          style={{
                            textAlign: "center",
                            backgroundColor: "#9cb23b",
                            color: "#fff",
                            padding: "5px 5px",
                            borderRadius: "5px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={seasonSide || match.course}
                        >
                          {seasonSide || match.course}
                        </p>
                      </div>

                      {/* Match Date + main season name (st1) */}
                      <div className="matchDate" style={{ width: "100%" }}>
                        <div
                          className="match-date"
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={`${match.date || ""}${seasonFullName ? ` ${seasonFullName}` : ""}`.trim()}
                        >
                          {match.date}
                          {/* {seasonTop ? ` ${seasonTop}` : ''} */}
                          {seasonFullName ? ` ${seasonFullName}` : ''}
                        </div>
                      </div>

                      {/* Match Status (W/L) */}
                      <div className="match-status">{match.outcome}</div>

                      {/* Score Table */}
                      <div className="score-list">
                        {(() => {
                          // Determine winner based on scores
                          const player1Scores = match.scores[0] || {};
                          const player2Scores = match.scores[1] || {};
                          
                          let player1Wins = 0;
                          let player2Wins = 0;
                          
                          // Compare sets
                          if (parseInt(player1Scores.set1) > parseInt(player2Scores.set1)) player1Wins++;
                          else if (parseInt(player2Scores.set1) > parseInt(player1Scores.set1)) player2Wins++;
                          
                          if (parseInt(player1Scores.set2) > parseInt(player2Scores.set2)) player1Wins++;
                          else if (parseInt(player2Scores.set2) > parseInt(player1Scores.set2)) player2Wins++;
                          
                          if (parseInt(player1Scores.set3) > parseInt(player2Scores.set3)) player1Wins++;
                          else if (parseInt(player2Scores.set3) > parseInt(player1Scores.set3)) player2Wins++;
                          
                          // Check for default_winner, opponentRetired, puttoff_winner
                          const currentPlayerId = match.player?.id || records[0]?.player?.id || memberId;
                          let isPlayer1Winner = player1Wins > player2Wins;
                          
                          if (match.default_winner) {
                            isPlayer1Winner = String(match.default_winner) === String(currentPlayerId);
                          } else if (match.opponentRetired) {
                            isPlayer1Winner = String(match.opponentRetired) !== String(currentPlayerId);
                          } else if (match.puttoff_winner) {
                            // BYE: puttoff_winner is winner ID; member (player) won if puttoff_winner === memberId
                            isPlayer1Winner = String(match.puttoff_winner) === String(currentPlayerId);
                          }
                          
                          // Determine which player is winner and loser
                          let winner = isPlayer1Winner ? match.player : match.opponent;
                          let loser = isPlayer1Winner ? match.opponent : match.player;
                          if (match.puttoff_winner && !winner) {
                            winner = match.player;
                            loser = match.opponent;
                          }
                          const winnerScores = isPlayer1Winner ? match.scores[0] : match.scores[1];
                          const loserScores = isPlayer1Winner ? match.scores[1] : match.scores[0];
                          const winnerIsHome = isPlayer1Winner;
                          const winnerSet3 = Number(winnerScores?.set3 || 0);
                          const loserSet3 = Number(loserScores?.set3 || 0);
                          const showThirdSet = winnerSet3 > 0 || loserSet3 > 0;
                          const isByeMatch = Boolean(match.puttoff_winner) || (Boolean(match.default_winner) && (match.opponent?.id == null));

                          const tb1 = resolveTiebreakSuperscript(
                            winnerScores?.set1,
                            loserScores?.set1,
                            winnerScores?.set1sup,
                            loserScores?.set1sup
                          );
                          const tb2 = resolveTiebreakSuperscript(
                            winnerScores?.set2,
                            loserScores?.set2,
                            winnerScores?.set2sup,
                            loserScores?.set2sup
                          );
                          const tb3 = resolveTiebreakSuperscript(
                            winnerScores?.set3,
                            loserScores?.set3,
                            winnerScores?.set3sup,
                            loserScores?.set3sup
                          );

                          const setCell = (games, oppGames, displaySup, forLoserRow) => {
                            const empty = forLoserRow && !(loser && loser.id != null);
                            const won = !empty && didWinSet(games, oppGames);
                            const value = empty
                              ? (isByeMatch ? 0 : "-")
                              : (games != null ? games : "-");
                            return (
                              <td className={won ? "strong runs" : ""}>
                                <span className="position-relative">
                                  {value}
                                  {!empty && displaySup != null ? <sup>{displaySup}</sup> : null}
                                </span>
                              </td>
                            );
                          };
                          
                          return (
                            <table className="centerAlign linescore">
                              <tbody>
                                {/* Winner Row (First) */}
                                <tr>
                                  <td className="leftAlign strong">
                                    <div className="infodiv_sec">
                                      <div>
                                        <img
                                          className="align-middle"
                                          style={{
                                            width: 40,
                                            borderRadius: "50%",
                                            marginRight: 8,
                                          }}
                                          src={`/${winner?.icon}`}
                                          alt="Winner"
                                        />
                                      </div>
                                      <div className="d-block w-100 g-textCol">
                                        {winner?.name} <br />
                                        <b className={winnerIsHome ? "homeTextVAl d-block pt-2" : "awayTextVAl d-block pt-2"}>
                                          {winnerIsHome ? "Home" : "Away"}
                                        </b>
                                      </div>
                                    </div>
                                  </td>
                                  {setCell(winnerScores?.set1, loserScores?.set1, tb1.topSup, false)}
                                  {setCell(winnerScores?.set2, loserScores?.set2, tb2.topSup, false)}
                                  {showThirdSet && setCell(winnerScores?.set3, loserScores?.set3, tb3.topSup, false)}
                                </tr>

                                {/* Loser Row (Second) - BYE: show "Bye" only, no Opponent/Away/icon */}
                                <tr>
                                  <td className="leftAlign strong">
                                    <div className="infodiv_sec">
                                      {loser && loser.id != null ? (
                                        <>
                                          <div>
                                            <img
                                              className="align-middle"
                                              style={{
                                                width: 40,
                                                borderRadius: "50%",
                                                marginRight: 8,
                                              }}
                                              src={`/${loser.icon}`}
                                              alt="Opponent"
                                            />
                                          </div>
                                          <div className="d-block">
                                            {loser.name} <br />
                                            <b className={!winnerIsHome ? "homeTextVAl d-block pt-2" : "awayTextVAl d-block pt-2"}>
                                              {!winnerIsHome ? "Home" : "Away"}
                                            </b>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="d-block" style={{ color: "#666" }}>
                                          
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  {setCell(loserScores?.set1, winnerScores?.set1, tb1.bottomSup, true)}
                                  {setCell(loserScores?.set2, winnerScores?.set2, tb2.bottomSup, true)}
                                  {showThirdSet && setCell(loserScores?.set3, winnerScores?.set3, tb3.bottomSup, true)}
                                </tr>
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>

                      <div className="retiredWin">
                        {(() => {
                          const currentPlayerId = match.player?.id || records[0]?.player?.id;
                          let statusMessage = null;

                          if (match.default_winner) {
                            if (String(match.default_winner) === String(currentPlayerId)) {
                              statusMessage = match.opponent?.id == null ? "Bye" : "Default Win";
                            } else {
                              statusMessage = "Default Loss";
                            }
                          } else if (match.opponentRetired) {
                            if (String(match.opponentRetired) === String(currentPlayerId)) {
                              statusMessage = "Opponent Retired";
                            } else {
                              statusMessage = "Opponent Retired";
                            }
                          } else if (match.puttoff_winner) {
                            statusMessage = "Bye";
                          }

                          return statusMessage ? (
                            <p style={{ color: "#666", marginTop: "10px", fontSize: "14px" }}>
                              {statusMessage}
                            </p>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="clear" />
            </div>
            </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Records;
